import { KeyboardEvent, ReactNode, useContext, useEffect, useRef, useState } from 'react'

import { TabsContext } from '../Tabs'

import { Tab, TabProps, TabRenderProps } from '.'

export type TabDefaultProps = Omit<TabProps, 'children'> & {
  children: ReactNode
}

export const TabDefault = ({ children, value }: TabDefaultProps) => {
  const { activeTab } = useContext(TabsContext)
  const ref = useRef<HTMLDivElement>(null)
  const [isActive, setIsActive] = useState(false)

  const handleKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    tabValues: TabRenderProps['tabValues'],
    setActiveTab: TabRenderProps['setActiveTab'],
  ) => {
    const currentIndex = tabValues.indexOf(value)
    const lastIndex = tabValues.length - 1

    switch (event.key) {
      case 'ArrowLeft':
        if (currentIndex > 0) {
          setActiveTab(tabValues[currentIndex - 1])
        }
        break
      case 'ArrowRight':
        if (currentIndex < lastIndex) {
          setActiveTab(tabValues[currentIndex + 1])
        }
        break
      case 'ArrowUp':
        if (currentIndex !== 0) {
          setActiveTab(tabValues[0])
        }
        break
      case 'ArrowDown':
        if (currentIndex !== lastIndex) {
          setActiveTab(tabValues[lastIndex])
        }
        break
      default:
    }
  }

  useEffect(() => {
    setIsActive(value === activeTab)
  }, [activeTab])

  useEffect(() => {
    if (isActive && document.activeElement !== ref.current) {
      ref.current?.focus()
    }
  }, [isActive, ref.current])

  return (
    <Tab value={value}>
      {({ setActiveTab, tabValues }) => (
        <Tab.Styled.Container
          aria-controls={value}
          aria-selected={isActive}
          id={`${value}-trigger`}
          isActive={isActive}
          onClick={() => setActiveTab(value)}
          onKeyDown={(event) => handleKeyDown(event, tabValues, setActiveTab)}
          ref={ref}
          role="tab"
          tabIndex={isActive ? 0 : -1}
        >
          {children}
        </Tab.Styled.Container>
      )}
    </Tab>
  )
}
